import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { select, Store } from '@ngrx/store';
import { combineLatest, interval, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as pbi from 'powerbi-client';
import { models } from 'powerbi-client';

import * as fromAuth from '../../../auth/store';
import * as fromRoot from '../../../core/store';
import { PowerbiReportApiModel } from '../../models/powerbi-report-api.model';
import { PowerbiService } from '../../../views/waiting-list/services/powerbi.service';

@Component({
  selector: 'ebo-powerbi-report',
  templateUrl: './powerbi-report.component.html',
  styleUrls: ['./powerbi-report.component.scss']
})
export class PowerbiReportComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() reportType = 0;
  @Input() showPageNavigation = false;

  @ViewChild('reportContainer', { static: false }) reportContainer: ElementRef;

  public isLoaded = false;
  public accessDenied = false;
  public accessDeniedMessage = '';

  private storeSub: Subscription;
  private tokenRefreshSub: Subscription;

  private currentEnv: string;
  private currentBotId: number;
  private report: pbi.Report;
  private tokenExpiresAt = 0;

  private readonly MINUTES_BEFORE_EXPIRATION = 10;
  private readonly TOKEN_CHECK_INTERVAL_MS = 30000;

  private powerbi = new pbi.service.Service(pbi.factories.hpmFactory, pbi.factories.wpmpFactory, pbi.factories.routerFactory);

  constructor(
    private store: Store<fromRoot.AppState>,
    private powerbiService: PowerbiService,
    private snackbar: MatSnackBar
  ) {}

  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  ngAfterViewInit(): void {
    this.storeSub = combineLatest([this.store.pipe(select(fromAuth.getEnvironment)), this.store.pipe(select(fromAuth.getBotId))])
      .pipe(filter(([env, botId]) => !!env && !!botId))
      .subscribe(([env, botId]) => {
        this.currentEnv = env.toLowerCase();
        this.currentBotId = botId;
        this.accessDenied = false;
        this.accessDeniedMessage = '';
        this.loadReport();
      });
  }

  private loadReport(): void {
    this.isLoaded = true;
    this.resetEmbeddedReport();

    this.powerbiService.getReport(this.getEnvironmentNumber(), this.reportType, this.currentBotId).subscribe(
      (result: PowerbiReportApiModel) => {
        if (result?.embedToken) {
          this.tokenExpiresAt = new Date(result.tokenExpiry).getTime();
          this.embedReport(result);
          this.startTokenRefreshMonitor();
        } else {
          this.snackbar.open('Failed to load Power BI report configuration', 'Dismiss', { duration: 5000 });
        }

        this.isLoaded = false;
      },
      error => {
        this.isLoaded = false;
        this.stopTokenRefreshMonitor();
        this.resetEmbeddedReport();

        if (error.status === 403 || error.status === 503) {
          this.accessDenied = true;
          this.accessDeniedMessage = this.powerbiService.getUserErrorMessage(
            error,
            error.status === 503
              ? 'Power BI Report is not available.'
              : 'You do not have permission to view this report.'
          );
        } else {
          this.snackbar.open(
            this.powerbiService.getUserErrorMessage(error, 'Unable to load the Power BI report. Please try again later.'),
            'Dismiss',
            { duration: 5000 }
          );
        }
      }
    );
  }

  private embedReport(embedInfo: PowerbiReportApiModel): void {
    if (!this.reportContainer?.nativeElement) {
      console.error('Power BI container not available yet.');
      return;
    }

    const config: models.IReportEmbedConfiguration = {
      type: 'report',
      id: embedInfo.reportId,
      embedUrl: embedInfo.embedUrl,
      accessToken: embedInfo.embedToken,
      tokenType: models.TokenType.Embed,
      permissions: models.Permissions.Read,
      viewMode: models.ViewMode.View,
      settings: {
        panes: {
          filters: { visible: false },
          pageNavigation: { visible: false }
        },
        background: models.BackgroundType.Transparent
      }
    };

    this.report = this.powerbi.embed(this.reportContainer.nativeElement, config) as pbi.Report;

    this.report.off('error');
    this.report.on('error', this.handleReportError);
    this.report.on('loaded', () => console.log('Power BI report loaded'));
    this.report.on('rendered', () => console.log('Power BI report rendered'));
  }

  private handleReportError = (event: any): void => {
    const error = event.detail;

    if (error.message === models.CommonErrorCodes.TokenExpired) {
      this.refreshToken();
    } else if (error.level === models.TraceType.Fatal) {
      this.accessDenied = true;
      this.accessDeniedMessage = 'Power BI Report is not available.';
    }
  };

  private refreshToken(): void {
    this.powerbiService.getReport(this.getEnvironmentNumber(), this.reportType, this.currentBotId).subscribe(
      async (result: PowerbiReportApiModel) => {
        if (result?.embedToken && this.report) {
          this.tokenExpiresAt = new Date(result.tokenExpiry).getTime();

          try {
            await this.report.setAccessToken(result.embedToken);
          } catch {
            this.snackbar.open('Failed to refresh report token. Please reload.', 'Dismiss', { duration: 5000 });
          }
        }
      },
      () => {
        this.snackbar.open('Failed to refresh report token. Please reload.', 'Dismiss', { duration: 5000 });
      }
    );
  }

  private startTokenRefreshMonitor(): void {
    this.stopTokenRefreshMonitor();
    this.tokenRefreshSub = interval(this.TOKEN_CHECK_INTERVAL_MS).subscribe(() => this.checkAndRefreshToken());
  }

  private stopTokenRefreshMonitor(): void {
    this.tokenRefreshSub?.unsubscribe();
    this.tokenRefreshSub = null;
  }

  private checkAndRefreshToken(): void {
    if (!this.tokenExpiresAt) {
      return;
    }

    const timeUntilExpiration = this.tokenExpiresAt - Date.now();
    const refreshThreshold = this.MINUTES_BEFORE_EXPIRATION * 60 * 1000;

    if (timeUntilExpiration <= refreshThreshold) {
      this.refreshToken();
    }
  }

  private handleVisibilityChange = (): void => {
    if (!document.hidden) {
      this.checkAndRefreshToken();
    }
  };

  private resetEmbeddedReport(): void {
    if (!this.reportContainer?.nativeElement) {
      return;
    }

    try {
      this.powerbi.reset(this.reportContainer.nativeElement);
    } catch (error) {
      console.error('Error cleaning up Power BI report:', error);
    } finally {
      this.report = null;
    }
  }

  private getEnvironmentNumber(): number {
    return this.currentEnv === 'test' ? 1 : 2;
  }

  ngOnDestroy(): void {
    this.storeSub?.unsubscribe();
    this.stopTokenRefreshMonitor();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.resetEmbeddedReport();
  }
}
